import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import api from '../api';

interface WrittenLessonCompletionProps {
  examType: 'WAEC' | 'JAMB';
  topicUUID: string;
  lessonIndex: number;
}

const WrittenLessonCompletion: React.FC<WrittenLessonCompletionProps> = ({ examType, topicUUID, lessonIndex }) => {
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    api.get('/lesson-video-progress/written', { params: { examType, topicUUID, lessonIndex } })
      .then((response) => {
        if (mounted) setCompleted(Boolean(response.data?.data?.completed));
      })
      .catch(() => {
        if (mounted) setError('Written lesson completion could not be loaded.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, [examType, topicUUID, lessonIndex]);

  const toggleCompletion = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await api.put('/lesson-video-progress/written', {
        examType,
        topicUUID,
        lessonIndex,
        completed: !completed,
      });
      setCompleted(Boolean(response.data?.data?.completed));
    } catch {
      setError('Written lesson completion could not be saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ my: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: completed ? 'success.light' : 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Written lesson: {completed ? 'Complete' : 'Not completed'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This reading progress is saved separately from the lesson video.
        </Typography>
      </Box>
      <Button
        variant={completed ? 'outlined' : 'contained'}
        color={completed ? 'success' : 'primary'}
        onClick={() => void toggleCompletion()}
        disabled={loading || saving}
        startIcon={saving ? <CircularProgress size={16} /> : completed ? <CheckCircleOutlineIcon /> : <RadioButtonUncheckedIcon />}
      >
        {loading ? 'Loading' : completed ? 'Mark as not complete' : 'Mark written lesson complete'}
      </Button>
      {error && <Alert severity="warning" sx={{ flexBasis: '100%' }}>{error}</Alert>}
    </Box>
  );
};

export default WrittenLessonCompletion;
